import React from 'react';

import * as Sentry from '@sentry/react';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';

import { fingerprintForThrown } from '@/lib/sentryFingerprint.util';
import { isAwsRumTelemetryNoise, isTransientFetchError } from '@/lib/sentryRumNoise.util';
import {
  isInternalHostname,
  scrubExternalQuery,
  scrubExternalUrl,
  scrubReferrer,
  scrubUrl,
} from '@/lib/sentryUrlScrub.util';

const SENTRY_DSN =
  'https://0b5b98071584c898b927d2b3a6b892dd@o4511490698248192.ingest.us.sentry.io/4511490702704640';

// GW-12474: Only the two canonical deploy hostnames may map to a real Sentry
// environment. Any other host (gwlocal.test, `npm run preview`, an IP, a
// headless/e2e origin) is treated as `local` REGARDLESS of build MODE — a
// production-mode (`vite build`) bundle served off a non-canonical host must
// never be tagged `environment=production`, or it pollutes the production
// Sentry stream with synthetic local noise (surfaced by GW-SPA-34).
export function getEnvironment(): string {
  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  if (host === 'platform.generalwisdom.com') return 'production';
  if (host === 'dev.generalwisdom.com') return 'development';
  if (host === 'localhost' || host === '127.0.0.1') return 'local';
  return 'local';
}

function getRelease(): string {
  if (typeof document === 'undefined') return 'dev';
  return document.querySelector('meta[name="build-sha"]')?.getAttribute('content') ?? 'dev';
}

const isE2ETestMode = import.meta.env.VITE_E2E_TEST_MODE === 'true';
const environment = getEnvironment();
const isProduction = environment === 'production';

/**
 * GW-9568: Redact PII from a `request.query_string` when it is a plain string.
 * `event.request.url` already carries the fully sanitized URL, so this is a
 * belt-and-suspenders pass; non-string union shapes are left untouched.
 */
function scrubQueryStringField(queryString: string): string {
  // scrubUrl fails closed on a bare query fragment, so prefix a placeholder path
  // then strip it back off, preserving the redaction of denylisted params.
  const scrubbed = scrubUrl(`/?${queryString}`);
  const queryIndex = scrubbed.indexOf('?');
  return queryIndex === -1 ? '' : scrubbed.slice(queryIndex + 1);
}

/**
 * GW-9568: Scrub PII from an event's `request` bag in place — the `url`, the
 * `Referer`/`referer` header (same-origin-guarded), and any string
 * `query_string`. Shared by both `beforeSend` (error events) and
 * `beforeSendTransaction` (performance events), since transactions carry the
 * same `request` shape and would otherwise leak the raw page URL.
 */
function scrubRequest(request: NonNullable<Sentry.ErrorEvent['request']> | undefined): void {
  if (!request) return;
  if (typeof request.url === 'string') {
    request.url = scrubUrl(request.url);
  }
  if (request.headers) {
    for (const headerKey of ['Referer', 'referer']) {
      const value = request.headers[headerKey];
      if (typeof value === 'string') {
        // GW-9568: the Referer header is a referrer, so apply the same-origin
        // guard (scrubReferrer) — cross-origin referrers pass through untouched
        // to preserve attribution; same-origin ones get q/email redacted.
        const scrubbed = scrubReferrer(value);
        if (typeof scrubbed === 'string') {
          request.headers[headerKey] = scrubbed;
        }
      }
    }
  }
  if (typeof request.query_string === 'string') {
    request.query_string = scrubQueryStringField(request.query_string);
  }
}

/**
 * GW-9568: Sentry `beforeSend` hook. Preserves the pre-existing drop/fingerprint
 * behavior EXACTLY and in order (RUM noise -> transient fetch -> fingerprint),
 * then — only for events that survive — scrubs PII from the request URL, the
 * Referer header, and any string `query_string`.
 */
export function scrubbingBeforeSend(
  event: Sentry.ErrorEvent,
  hint: Sentry.EventHint
): Sentry.ErrorEvent | null {
  // GW-8468: Filter out AWS RUM telemetry noise (CWR: prefixed errors or aws-rum-web stack frames)
  if (isAwsRumTelemetryNoise(hint?.originalException)) {
    return null; // Drop the event
  }

  if (isTransientFetchError(hint?.originalException)) {
    return null; // GW-9112: drop transient "Failed to fetch" TypeErrors before send
  }

  const fingerprint = fingerprintForThrown(hint?.originalException);
  if (fingerprint) event.fingerprint = fingerprint;

  // GW-9568: scrub PII from URL-bearing request fields (only reached for
  // non-dropped events). event.request.url is the canonical carrier.
  scrubRequest(event.request);

  return event;
}

/**
 * GW-9568: Sentry `beforeBreadcrumb` hook. Navigation/fetch/xhr breadcrumbs carry
 * URLs on `data.url`, `data.from`, and `data.to`; scrub each through
 * `scrubExternalUrl` so breadcrumb trails don't leak PII.
 *
 * GW-9675: `data.url` on a `fetch`/`xhr` breadcrumb can point at an EXTERNAL
 * host (e.g. the Google Analytics `/g/collect` beacon carrying
 * `ep.search_term=<raw q>`). `scrubExternalUrl` redacts every param value on any
 * host that is not the page host or a subdomain of it, while internal URLs and
 * relative nav paths (`from`/`to`) still get the narrow q/email denylist
 * treatment.
 */
export function scrubbingBeforeBreadcrumb(
  breadcrumb: Sentry.Breadcrumb,
  _hint?: Sentry.BreadcrumbHint
): Sentry.Breadcrumb | null {
  const data = breadcrumb.data;
  if (data) {
    for (const field of ['url', 'from', 'to']) {
      const value = data[field];
      if (typeof value === 'string') {
        data[field] = scrubExternalUrl(value);
      }
    }
  }
  return breadcrumb;
}

/**
 * GW-9568: URL-bearing keys on a Sentry span's / trace context's `data` bag.
 * The browser-tracing integration populates these with full URLs (incl. query
 * strings), so they carry the same `?q=`/`?email=` PII as `event.request.url`
 * does on error events. `http.query` is a bare query fragment; the rest are full
 * URLs. First-party URLs use the denylist + `sensitive=t` marker; third-party
 * URLs (GW-9675) have every value redacted — see `scrubSpanData`.
 */
const SPAN_URL_DATA_KEYS = ['url', 'http.url', 'url.full'] as const;
const SPAN_QUERY_DATA_KEYS = ['http.query'] as const;

/**
 * GW-9568: Scrub URL-bearing string fields on a span/trace `data` bag in place.
 * Non-string values (status codes, durations, etc.) are left untouched.
 *
 * GW-9675: full URLs (`url`/`http.url`/`url.full`) go through `scrubExternalUrl`,
 * which redacts every param value on an external host (e.g. a Google Analytics
 * `/g/collect` beacon) and applies the q/email denylist on an internal host.
 * `http.query` is a bare fragment with no host of its own, so we infer the span's
 * host from its URL keys: on an external span every param value is redacted
 * (keys kept); on an internal span the q/email denylist is applied (matching the
 * full-URL treatment).
 */
function scrubSpanData(data: Record<string, unknown> | undefined): void {
  if (!data) return;
  for (const key of SPAN_URL_DATA_KEYS) {
    const value = data[key];
    if (typeof value === 'string') {
      data[key] = scrubExternalUrl(value);
    }
  }
  const spanIsExternal = isExternalSpan(data);
  for (const key of SPAN_QUERY_DATA_KEYS) {
    const value = data[key];
    if (typeof value === 'string') {
      data[key] = spanIsExternal
        ? scrubExternalQuery(value)
        : scrubQueryStringField(value.replace(/^\?/, ''));
    }
  }
}

/**
 * GW-9675: decide whether a span's `http.query` belongs to an external host.
 * `http.query` carries no host, so read it from the span's full-URL keys. Any
 * internal URL key means treat the bare query as internal (denylist); otherwise,
 * if a URL key exists and is external, redact every value. With no parseable URL
 * key at all, default to the internal denylist (the prior, safe behavior) rather
 * than over-redacting an unknown same-origin fragment.
 */
function isExternalSpan(data: Record<string, unknown>): boolean {
  let sawExternal = false;
  for (const key of SPAN_URL_DATA_KEYS) {
    const value = data[key];
    if (typeof value !== 'string') continue;
    try {
      const host = new URL(value).hostname;
      if (isInternalHostname(host)) return false;
      sawExternal = true;
    } catch {
      // Relative/unparseable URL key => host-less => internal.
      return false;
    }
  }
  return sawExternal;
}

/**
 * GW-9568: Sentry `beforeSendTransaction` hook. Performance transactions are a
 * SEPARATE pipeline from error events — they never pass through `beforeSend` —
 * so the browser-tracing integration would otherwise ship the raw pageload URL
 * (`contexts.trace.data['url.full']`), every span's request URL/query
 * (`span.data.url` / `http.url` / `http.query` / `url.full`), and URL-shaped
 * span descriptions to Sentry with `?q=`/`?email=` intact. Scrub all of them
 * through the same `scrubUrl` denylist (+ GW-9673 `sensitive=t` marker) used for
 * error events, so the two pipelines redact identically.
 */
export function scrubbingBeforeSendTransaction<T extends Sentry.Event>(
  // `@sentry/react` does not re-export `TransactionEvent` (only `ErrorEvent`),
  // and `TransactionEvent` is just `Event & { type: 'transaction' }`. `Event`
  // already carries the `request`, `contexts.trace`, and `spans` fields this
  // hook touches, so we type against a generic `Event` subtype rather than
  // reaching into the transitive `@sentry/core`. The generic preserves the
  // concrete event type so this stays assignable to Sentry's
  // `beforeSendTransaction: (TransactionEvent) => TransactionEvent` option.
  event: T
): T {
  // Transactions carry the same `request` bag as error events (with the raw
  // pageload URL), so scrub it identically to beforeSend.
  scrubRequest(event.request);
  scrubSpanData(event.contexts?.trace?.data as Record<string, unknown> | undefined);

  if (event.spans) {
    for (const span of event.spans) {
      scrubSpanData(span.data as Record<string, unknown> | undefined);
      // A span description is often the request URL (e.g. fetch/xhr spans).
      // Scrub it only when it parses as a URL so non-URL descriptions
      // (component names, SQL, etc.) are left verbatim. GW-9675: route through
      // scrubExternalUrl so a third-party beacon description (e.g. the GA
      // /g/collect URL) is fully stripped, not just q/email-denylisted.
      if (typeof span.description === 'string' && /^https?:\/\//i.test(span.description)) {
        span.description = scrubExternalUrl(span.description);
      }
    }
  }

  return event;
}

if (!isE2ETestMode) {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment,
    release: getRelease(),
    sendDefaultPii: false,
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      // GW-11884: Session Replay is intentionally NOT added here. Core Sentry init
      // (error capture + tracing, above) stays EAGER so early-boot errors/traces are
      // never lost, but the heavy Replay integration (rrweb recorder + compression
      // worker) is deferred to after first paint via `src/lib/sentryReplay.util.ts`,
      // which main.tsx dynamically imports from an idle callback. Deferring it here
      // (rather than in the integrations array) is what lets the bundler split the
      // replay code into its own async `sentry-replay-vendor` chunk.
      //
      // GW-9673 (still applies once Replay is added): Session replay does NOT capture
      // the raw address-bar URL independently of breadcrumbs in @sentry/replay@10.x.
      // Replay navigation events surface as Sentry navigation breadcrumbs, which route
      // through `scrubbingBeforeBreadcrumb` -> scrubUrl -> redactParams, so the
      // sensitive=t marker + denylist redaction already cover them. maskAllText +
      // blockAllMedia + breadcrumb scrubbing is sufficient; no additional replay-side
      // URL sanitizer is required (the deferred loader sets maskAllText/blockAllMedia).
    ],
    // GW-6428: Group ApiError events by (status, normalized route) so backend
    // errors don't all collapse into the single BaseApiClient.throwApiError stack.
    // GW-9568: also scrubs PII (q/email) from request URL/headers/query_string.
    beforeSend: scrubbingBeforeSend,
    // GW-9568: scrub PII from URL-bearing breadcrumb fields.
    beforeBreadcrumb: scrubbingBeforeBreadcrumb,
    // GW-9568: performance transactions are a separate pipeline from beforeSend;
    // scrub the pageload trace URL + span URLs/queries/descriptions too.
    beforeSendTransaction: scrubbingBeforeSendTransaction,
    tracesSampleRate: isProduction ? 0.1 : 1.0,
    tracePropagationTargets: [
      'localhost',
      /^https:\/\/api\.dev\.generalwisdom\.com/,
      /^\/dev-api/,
      /^\/dev-osint/,
    ],
    replaysSessionSampleRate: isProduction ? 0.05 : 0.1,
    replaysOnErrorSampleRate: 1.0,
  });
}
